import { assign, isNull } from 'lodash';
import { Logger } from 'vue-infra';
import { NAMESPACE, WIDGET_AC_COMPONENT_NAME } from '@/common/consts';
import { DATA_STATES } from '@/store/consts';
import ACService from '@/services/ac/ACService';
import { actions as baseActions, ACTION_TYPES as BASE_ACTION_TYPES } from '../base-collection/actions';
import { MUTATION_TYPES } from './mutations';
import { STATE_KEYS } from './state';
import { CONNECTION_TEST_RESULT_STATE } from './helper';
import widgetTransformer from '../../../../services/widget/widgetTransformer';

const logger = Logger.getLogger('widget-configuration');

const ACTION_TYPES = assign({}, BASE_ACTION_TYPES, {
  SET_CURRENT_WIDGET_ID: 'setCurrentWidgetId',
  SET_CONFIGURATION_STEP_IDX: 'setConfigurationStepIndex',
  SET_CONFIGURATION_STEP_BY_NAME: 'setConfigurationStepByName',
  SET_WIDGET_CUSTOMIZATION: 'setWidgetCustomizationStep',
  FETCH_WIDGETS: 'fetchWidgets',
  FETCH_WIDGET_CONNECTION: 'fetchWidgetConnection',
  FETCH_WIDGET_VIEW: 'fetchWidgetView',
  FETCH_WIDGET_LAYOUT: 'fetchWidgetLayout',
  SET_WIDGET_CONNECTION: 'setWidgetConnection',
  SAVE_WIDGET_CONNECTION: 'saveWidgetConnection',
  SAVE_WIDGET_VIEW: 'saveWidgetView',
  SAVE_WIDGET_LAYOUT: 'saveWidgetLayout',
  TEST_WIDGET_CONNECTION: 'testWidgetConnection',
  TEST_RESET: 'resetTestResult',
  DELETE_WIDGET_CONNECTION: 'deleteWidgetConnection',
  SAVE_OR_UPDATE_WIDGET: 'saveOrUpdateWidget',
  FETCH_EXPLORER: 'fetchExplorer',
  FETCH_OBJECT: 'fetchObject',
  FETCH_FIELD: 'fetchField',
  FETCH_TOS_AGREEMENT: 'fetchToSAgreement',
  AGREE_TO_TOS: 'agreeToToS',
});

const actions = assign({}, baseActions, {
  [ACTION_TYPES.SET_CURRENT_WIDGET_ID]({ commit }, widgetId) {
    if (isNull(widgetId)) {
      commit(MUTATION_TYPES.RESET);
    } else {
      commit(MUTATION_TYPES.SET_CURRENT_WIDGET_ID, widgetId);
    }
  },
  [ACTION_TYPES.SET_CONFIGURATION_STEP_IDX]({ commit }, idx) {
    commit(MUTATION_TYPES.SET_WIDGET_CONFIGURATION_STEP_IDX, idx);
  },
  [ACTION_TYPES.SET_CONFIGURATION_STEP_BY_NAME]({ commit, state }, stepName) {
    const allSteps = [...state[STATE_KEYS.ALL_CONFIGURATION_STEPS]];
    const foundIdx = allSteps.findIndex(({ name }) => name === stepName);
    if (foundIdx > -1) {
      commit(MUTATION_TYPES.SET_WIDGET_CONFIGURATION_STEP_IDX, foundIdx);
    }
  },
  async [ACTION_TYPES.SET_WIDGET_CUSTOMIZATION]({ commit }, customizationFields) {
    commit(MUTATION_TYPES.SET_WIDGET_CUSTOMIZATION, customizationFields);
  },
  async [ACTION_TYPES.SAVE_OR_UPDATE_WIDGET]({ commit, dispatch }, widgetACConfiguration) {
    commit(MUTATION_TYPES.SET_DATA_STATE, DATA_STATES.LOADING);
    try {
      const { 'ac-revision': acRevision, widgets } = await ACService.getAllWidgets();
      const existingWidget = widgets.find(({ componentName }) => componentName === WIDGET_AC_COMPONENT_NAME);

      if (existingWidget) {
        await ACService.updateWidget({
          'ac-revision': acRevision, widgetConfiguration: { id: existingWidget.id, ...widgetACConfiguration },
        });
      } else {
        await ACService.createWidget(widgetACConfiguration);
      }

      commit(MUTATION_TYPES.SET_DATA_STATE, DATA_STATES.SUCCESS);
      dispatch(ACTION_TYPES.FETCH_WIDGETS);
    } catch (error) {
      commit(MUTATION_TYPES.SET_DATA_STATE, DATA_STATES.ERROR);
      console.error('error while putting', error);
      logger.error('Error saving widget: ', `${NAMESPACE.IHUB}: saveWidget`, error);
    }
  },
  async [ACTION_TYPES.FETCH_WIDGETS]({ commit }) {
    commit(MUTATION_TYPES.SET_DATA_STATE, DATA_STATES.LOADING);
    try {
      const { widgets } = await ACService.getAllWidgets();
      commit(MUTATION_TYPES.SET_WIDGETS, widgets);
      commit(MUTATION_TYPES.SET_DATA_STATE, DATA_STATES.SUCCESS);
    } catch (error) {
      commit(MUTATION_TYPES.SET_DATA_STATE, DATA_STATES.ERROR);
      logger.error('Error fetching widget: ', `${NAMESPACE.IHUB}: fetchWidget`, error);
    }
  },

  async [ACTION_TYPES.FETCH_WIDGET_CONNECTION]({ commit, state }) {
    commit(MUTATION_TYPES.SET_DATA_STATE, DATA_STATES.LOADING);
    try {
      const connection = await state[STATE_KEYS.CURRENT_SERVICE].getConnection();
      commit(MUTATION_TYPES.SET_WIDGET_CONNECTION, connection);
      commit(MUTATION_TYPES.SET_DATA_STATE, DATA_STATES.SUCCESS);
    } catch (error) {
      const widgetConnectionResponse = widgetTransformer.transformWidgetConnectionResponse(error.response);
      if (widgetConnectionResponse.connection) {
        commit(MUTATION_TYPES.SET_WIDGET_CONNECTION, widgetConnectionResponse);
      }
      commit(MUTATION_TYPES.SET_DATA_STATE, DATA_STATES.ERROR);
      logger.error('Error fetching widget connection: ', `${NAMESPACE.IHUB}: fetchWidgetConnection`, error);
    }
  },
  async [ACTION_TYPES.FETCH_WIDGET_VIEW]({ commit, state }, viewId) {
    commit(MUTATION_TYPES.SET_DATA_STATE, DATA_STATES.LOADING);
    try {
      const view = await state[STATE_KEYS.CURRENT_SERVICE].getView(viewId);
      const widgetView = state[STATE_KEYS.WIDGET_VIEW];
      widgetView[viewId] = view;
      commit(MUTATION_TYPES.SET_WIDGET_VIEW, widgetView);
      commit(MUTATION_TYPES.SET_DATA_STATE, DATA_STATES.SUCCESS);
    } catch (error) {
      const widgetView = state[STATE_KEYS.WIDGET_VIEW];
      widgetView[viewId] = null;
      commit(MUTATION_TYPES.SET_WIDGET_VIEW, widgetView);
      commit(MUTATION_TYPES.SET_DATA_STATE, DATA_STATES.ERROR);
      logger.error('Error fetching widget connection: ', `${NAMESPACE.IHUB}: fetchWidgetConnection`, error);
    }
  },
  async [ACTION_TYPES.FETCH_WIDGET_LAYOUT]({ commit, state }, layoutPathVariable) {
    commit(MUTATION_TYPES.SET_DATA_STATE, DATA_STATES.LOADING);
    try {
      const layout = await state[STATE_KEYS.CURRENT_SERVICE].getLayout(layoutPathVariable.viewId, layoutPathVariable.layoutId);
      const widgetLayout = state[STATE_KEYS.WIDGET_LAYOUT];
      if (!widgetLayout[layoutPathVariable.viewId]) {
        widgetLayout[layoutPathVariable.viewId] = {};
      }
      widgetLayout[layoutPathVariable.viewId][layoutPathVariable.layoutId] = layout;
      commit(MUTATION_TYPES.SET_WIDGET_LAYOUT, widgetLayout);
      commit(MUTATION_TYPES.SET_DATA_STATE, DATA_STATES.SUCCESS);
    } catch (error) {
      const widgetLayout = state[STATE_KEYS.WIDGET_LAYOUT];
      if (!widgetLayout[layoutPathVariable.viewId]) {
        widgetLayout[layoutPathVariable.viewId] = {};
      }
      widgetLayout[layoutPathVariable.viewId][layoutPathVariable.layoutId] = null;
      commit(MUTATION_TYPES.SET_WIDGET_LAYOUT, widgetLayout);
      commit(MUTATION_TYPES.SET_DATA_STATE, DATA_STATES.ERROR);
      logger.error('Error fetching widget connection: ', `${NAMESPACE.IHUB}: fetchWidgetConnection`, error);
    }
  },
  [ACTION_TYPES.SET_WIDGET_CONNECTION]({ commit }, widgetConnection) {
    commit(MUTATION_TYPES.SET_CONNECTION_TEST_RESULT, null);
    commit(MUTATION_TYPES.SET_WIDGET_CONNECTION, widgetConnection);
  },
  async [ACTION_TYPES.SAVE_WIDGET_CONNECTION]({ commit, state }, requestBody) {
    commit(MUTATION_TYPES.SET_DATA_STATE, DATA_STATES.LOADING);
    try {
      let connection;
      try {
        connection = await state[STATE_KEYS.CURRENT_SERVICE].getConnection();
      } catch (error) {
        console.error(error);
      }

      if (connection) {
        await state[STATE_KEYS.CURRENT_SERVICE].updateWidgetConnection(requestBody);
      } else {
        await state[STATE_KEYS.CURRENT_SERVICE].saveWidgetConnection(requestBody);
      }
      commit(MUTATION_TYPES.SET_DATA_STATE, DATA_STATES.SUCCESS);
      commit(MUTATION_TYPES.SET_CONNECTION_TEST_RESULT, CONNECTION_TEST_RESULT_STATE.SUCCESS);
    } catch (error) {
      commit(MUTATION_TYPES.SET_DATA_STATE, DATA_STATES.ERROR);
      commit(MUTATION_TYPES.SET_CONNECTION_TEST_RESULT, CONNECTION_TEST_RESULT_STATE.FAILURE);
      logger.error('Error saving connection config: ', `${NAMESPACE.IHUB}: saveWidgetConnection`, error);
    }
  },
  async [ACTION_TYPES.SAVE_WIDGET_VIEW]({ commit, state }, viewRequestBody) {
    commit(MUTATION_TYPES.SET_DATA_STATE, DATA_STATES.LOADING);
    try {
      await state[STATE_KEYS.CURRENT_SERVICE].saveWidgetView(viewRequestBody.requestBody, viewRequestBody.viewId);
      commit(MUTATION_TYPES.SET_DATA_STATE, DATA_STATES.SUCCESS);
    } catch (error) {
      commit(MUTATION_TYPES.SET_DATA_STATE, DATA_STATES.ERROR);
      logger.error('Error saving view config: ', `${NAMESPACE.IHUB}: saveWidgetView`, error);
    }
  },
  async [ACTION_TYPES.SAVE_WIDGET_LAYOUT]({ commit, state }, layoutRequestBody) {
    commit(MUTATION_TYPES.SET_DATA_STATE, DATA_STATES.LOADING);
    try {
      await state[STATE_KEYS.CURRENT_SERVICE].saveWidgetLayout(layoutRequestBody.requestBody,
        layoutRequestBody.viewId, layoutRequestBody.layoutId);
      commit(MUTATION_TYPES.SET_DATA_STATE, DATA_STATES.SUCCESS);
    } catch (error) {
      commit(MUTATION_TYPES.SET_DATA_STATE, DATA_STATES.ERROR);
      logger.error('Error saving layout config: ', `${NAMESPACE.IHUB}: saveWidgetLayout`, error);
    }
  },
  async [ACTION_TYPES.TEST_WIDGET_CONNECTION]({ commit, state }) {
    commit(MUTATION_TYPES.SET_DATA_STATE, DATA_STATES.LOADING);
    try {
      const service = state[STATE_KEYS.CURRENT_SERVICE];
      const result = await service.testConnection();
      console.info(result);
      debugger;
      commit(MUTATION_TYPES.SET_CONNECTION_TEST_RESULT, (result) ? CONNECTION_TEST_RESULT_STATE.SUCCESS : CONNECTION_TEST_RESULT_STATE.FAILURE);
      commit(MUTATION_TYPES.SET_DATA_STATE, DATA_STATES.SUCCESS);
    } catch (error) {
      commit(MUTATION_TYPES.SET_CONNECTION_TEST_RESULT, CONNECTION_TEST_RESULT_STATE.FAILURE);
      commit(MUTATION_TYPES.SET_DATA_STATE, DATA_STATES.ERROR);
      logger.error('Error testing widget config: ', `${NAMESPACE.IHUB}: testConnection`, error);
    }
  },
  async [ACTION_TYPES.TEST_RESET]({ commit }) {
    commit(MUTATION_TYPES.SET_CONNECTION_TEST_RESULT, null);
  },
  async [ACTION_TYPES.DELETE_WIDGET_CONNECTION]({ commit, state }) {
    commit(MUTATION_TYPES.SET_DATA_STATE, DATA_STATES.LOADING);
    try {
      const service = state[STATE_KEYS.CURRENT_SERVICE];
      const response = await service.deleteConnection();
      commit(MUTATION_TYPES.SET_DELETE_RESPONSE, response);
      commit(MUTATION_TYPES.SET_DATA_STATE, DATA_STATES.SUCCESS);
    } catch (error) {
      commit(MUTATION_TYPES.SET_DELETE_RESPONSE, null);
      commit(MUTATION_TYPES.SET_DATA_STATE, DATA_STATES.ERROR);
      logger.error('Error deleting widget config: ', `${NAMESPACE.IHUB}: deleteConnection`, error);
    }
  },
  async [ACTION_TYPES.FETCH_EXPLORER]({ commit, state }) {
    commit(MUTATION_TYPES.SET_DATA_STATE, DATA_STATES.LOADING);
    try {
      const service = state[STATE_KEYS.CURRENT_SERVICE];
      const response = await service.getExplorer();
      commit(MUTATION_TYPES.SET_EXPLORER_RESPONSE, response);
      commit(MUTATION_TYPES.SET_DATA_STATE, DATA_STATES.SUCCESS);
    } catch (error) {
      commit(MUTATION_TYPES.SET_EXPLORER_RESPONSE, null);
      commit(MUTATION_TYPES.SET_DATA_STATE, DATA_STATES.ERROR);
      logger.error('Error fetching explorer: ', `${NAMESPACE.IHUB}: getExplorer`, error);
    }
  },
  async [ACTION_TYPES.FETCH_OBJECT]({ commit, state }, explorerPathVariable) {
    return new Promise(async (resolve, reject) => {
      commit(MUTATION_TYPES.SET_DATA_STATE, DATA_STATES.LOADING);
      if (!explorerPathVariable.exploreObject) {
        logger.error('Incorrect explorer object path variable: ', `${NAMESPACE.IHUB}: ${explorerPathVariable.exploreObject}`);
        return;
      }
      try {
        const service = state[STATE_KEYS.CURRENT_SERVICE];
        const result = await service.getObject(explorerPathVariable.exploreObject);
        commit(MUTATION_TYPES.SET_DATA_STATE, DATA_STATES.SUCCESS);
        resolve(result);
      } catch (error) {
        commit(MUTATION_TYPES.SET_DATA_STATE, DATA_STATES.ERROR);
        logger.error('Error fetching object: ', `${NAMESPACE.IHUB}: ${explorerPathVariable.exploreObject}`, error);
        reject(error);
      }
    });
  },
  async [ACTION_TYPES.FETCH_FIELD]({ commit, state }, explorerPathVariable) {
    return new Promise(async (resolve, reject) => {
      commit(MUTATION_TYPES.SET_DATA_STATE, DATA_STATES.LOADING);
      if (!explorerPathVariable.exploreObject) {
        logger.error('Incorrect explorer object path variable: ', `${NAMESPACE.IHUB}: ${explorerPathVariable.exploreObject}`);
        return;
      } else if (!explorerPathVariable.exploreField) {
        logger.error('Incorrect explorer field path variable: ', `${NAMESPACE.IHUB}: ${explorerPathVariable.exploreField}`);
        return;
      }
      try {
        const service = state[STATE_KEYS.CURRENT_SERVICE];
        const result = await service.getField(explorerPathVariable.exploreObject, explorerPathVariable.exploreField);
        commit(MUTATION_TYPES.SET_DATA_STATE, DATA_STATES.SUCCESS);
        resolve(result);
      } catch (error) {
        commit(MUTATION_TYPES.SET_DATA_STATE, DATA_STATES.ERROR);
        logger.error('Error fetching field: ', `${NAMESPACE.IHUB}: ${explorerPathVariable.exploreField}`, error);
        reject(error);
      }
    });
  },
  async [ACTION_TYPES.FETCH_TOS_AGREEMENT]({ state }) {
    return new Promise(async (resolve, reject) => {
      try {
        const service = state[STATE_KEYS.CURRENT_SERVICE];
        const { data } = await service.getToSAgreement();
        resolve(data);
      } catch (error) {
        logger.error('Error fetching ToS: ', error);
        reject(error);
      }
    });
  },
  async [ACTION_TYPES.AGREE_TO_TOS]({ state }, id) {
    return new Promise(async (resolve, reject) => {
      try {
        const service = state[STATE_KEYS.CURRENT_SERVICE];
        const result = await service.agreeToToS(id);
        resolve(result);
      } catch (error) {
        logger.error('Error agreeing to ToS: ', error);
        reject(error);
      }
    });
  },
});

export {
  actions,
  ACTION_TYPES,
};
